import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQaCapabilities } from "@/hooks/useQaCapabilities";
import { QaConfirmDialog } from "./QaConfirmDialog";
import { FlaskConical, RotateCcw, Unlock, Eraser, Calculator, Bug, ChevronDown, ChevronUp } from "lucide-react";

interface Props {
  assessmentId: string;
  onMutated?: () => void;
}

type ActionKey = "reset" | "reopen" | "clear" | "rescore" | null;

export function QaToolkitPanel({ assessmentId, onMutated }: Props) {
  const { loading, enabled } = useQaCapabilities();
  const { toast } = useToast();
  const [debug, setDebug] = useState<any>(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState<ActionKey>(null);

  if (loading || !enabled) return null;

  const env = (import.meta as any).env?.MODE || "unknown";

  const handle = async (rpc: string, args: Record<string, any>, label: string) => {
    setBusy(true);
    try {
      const { data, error } = await (supabase.rpc as any)(rpc, args);
      if (error) throw error;
      if (data && typeof data === "object" && (data as any).unsupported) {
        toast({
          title: `${label} not applicable`,
          description:
            (data as any).message ||
            `Re-run scoring is not available for ${(data as any).questionnaire_id ?? "this questionnaire"}.`,
        });
      } else if (data && typeof data === "object" && (data as any).error) {
        toast({ title: `${label} failed`, description: String((data as any).error), variant: "destructive" });
      } else {
        toast({ title: `${label} complete`, description: JSON.stringify(data).slice(0, 140) });
      }
      onMutated?.();
    } catch (e: any) {
      toast({ title: `${label} failed`, description: e.message || String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const loadDebug = async () => {
    setBusy(true);
    try {
      const { data, error } = await (supabase.rpc as any)("qa_get_assessment_debug", {
        p_assessment_id: assessmentId,
      });
      if (error) throw error;
      setDebug(data);
      setDebugOpen(true);
    } catch (e: any) {
      toast({ title: "Debug load failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card className="border-amber-500/60 bg-amber-50/30 dark:bg-amber-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FlaskConical className="h-4 w-4 text-amber-600" />
            QA Tools
            <Badge variant="outline" className="border-amber-600 text-amber-700">
              {env}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert className="border-amber-500/60">
            <AlertDescription className="text-xs">
              Internal testing tools. Scoped to assessments you are assigned to. All actions are
              audited to <code>qa_audit_log</code>.
            </AlertDescription>
          </Alert>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={loadDebug}>
              <Bug className="h-3.5 w-3.5 mr-1.5" />
              View Debug Metadata
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setAction("rescore")}>
              <Calculator className="h-3.5 w-3.5 mr-1.5" />
              Re-run Scoring
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setAction("reset")}>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset to Draft
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setAction("reopen")}>
              <Unlock className="h-3.5 w-3.5 mr-1.5" />
              Reopen Submitted
            </Button>
            <Button size="sm" variant="destructive" disabled={busy} onClick={() => setAction("clear")}>
              <Eraser className="h-3.5 w-3.5 mr-1.5" />
              Clear Responses
            </Button>
          </div>

          {debug && (
            <div className="border border-amber-200 rounded p-3 bg-background">
              <button
                className="flex items-center gap-1 text-xs font-medium"
                onClick={() => setDebugOpen((o) => !o)}
              >
                {debugOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                Debug Metadata
              </button>
              {debugOpen && (
                <pre className="mt-2 text-xs overflow-x-auto">{JSON.stringify(debug, null, 2)}</pre>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <QaConfirmDialog
        open={action === "reset"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Reset assessment to Draft?"
        description="Sets status to Draft and clears submitted/reviewed timestamps. Responses are preserved."
        confirmLabel="Reset to Draft"
        destructive
        onConfirm={(reason) =>
          handle("qa_reset_assessment_to_draft", { p_assessment_id: assessmentId, p_reason: reason, p_environment: env }, "Reset")
        }
      />
      <QaConfirmDialog
        open={action === "reopen"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Reopen submitted assessment?"
        description="Moves Submitted/In Review/Closed assessments back to Draft so they can be edited."
        confirmLabel="Reopen"
        destructive
        onConfirm={(reason) =>
          handle("qa_reopen_submitted_assessment", { p_assessment_id: assessmentId, p_reason: reason, p_environment: env }, "Reopen")
        }
      />
      <QaConfirmDialog
        open={action === "clear"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Clear all responses?"
        description="Deletes responses, reviewer ratings, and cached CSV/NIST scores for this assessment. The assessment row is preserved. This cannot be undone."
        confirmLabel="Clear Responses"
        destructive
        onConfirm={(reason) =>
          handle("qa_clear_responses", { p_assessment_id: assessmentId, p_reason: reason, p_environment: env }, "Clear")
        }
      />
      <QaConfirmDialog
        open={action === "rescore"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Re-run scoring?"
        description="Recomputes cached CSV domain and NIST function scores from current responses. Non-destructive."
        confirmLabel="Re-run Scoring"
        reasonRequired={false}
        onConfirm={() =>
          handle("qa_rerun_scoring", { p_assessment_id: assessmentId, p_environment: env }, "Re-run Scoring")
        }
      />
    </>
  );
}
